/**
 * Notification System Enums
 *
 * Defines all notification-related enums used across the platform
 */

/**
 * Notification Events
 * Events that can trigger notifications
 */
export enum NotificationEvent {
  // ===== Document Intelligence =====
  DOCUMENT_UPLOADED = 'document.uploaded',
  DOCUMENT_PROCESSED = 'document.processed',
  DOCUMENT_FAILED = 'document.failed',
  DOCUMENT_ANALYZED = 'document.analyzed',
  DOCUMENT_APPROVED = 'document.approved',
  DOCUMENT_REJECTED = 'document.rejected',
  FINDING_CREATED = 'finding.created',
  FINDING_CRITICAL = 'finding.critical', // High-priority finding

  // ===== User Management =====
  USER_INVITED = 'user.invited',
  USER_REGISTERED = 'user.registered',
  USER_LOGIN = 'user.login',
  USER_DEACTIVATED = 'user.deactivated',
  USER_REACTIVATED = 'user.reactivated',
  PASSWORD_RESET_REQUESTED = 'password.reset_requested',
  PASSWORD_RESET_COMPLETED = 'password.reset_completed',
  PASSWORD_CHANGED = 'password.changed',

  // ===== Employee / Team Invitations =====
  EMPLOYEE_INVITED = 'employee.invited',
  EMPLOYEE_INVITE_ACCEPTED = 'employee.invite_accepted',
  EMPLOYEE_INVITE_CANCELLED = 'employee.invite_cancelled',

  // ===== Consumer Invitations =====
  CONSUMER_INVITED = 'consumer.invited',
  CONSUMER_INVITE_ACCEPTED = 'consumer.invite_accepted',

  // ===== Realm Membership =====
  REALM_MEMBER_INVITED = 'realm.member_invited',
  REALM_MEMBER_JOINED = 'realm.member_joined',
  REALM_MEMBER_REMOVED = 'realm.member_removed',

  // ===== Authentication & Security =====
  MFA_ENABLED = 'mfa.enabled',
  MFA_DISABLED = 'mfa.disabled',
  MFA_ADMIN_OVERRIDE = 'mfa.admin_override',
  LOGIN_FROM_NEW_DEVICE = 'login.new_device',
  ALL_DEVICES_REVOKED = 'security.all_devices_revoked',
  SUSPICIOUS_ACTIVITY = 'security.suspicious_activity',
  ACCOUNT_LOCKED = 'security.account_locked',

  // ===== Agent Activity =====
  AGENT_CREATED = 'agent.created',
  AGENT_DEPLOYED = 'agent.deployed',
  AGENT_ERROR = 'agent.error',
  AGENT_RATE_LIMIT_EXCEEDED = 'agent.rate_limit_exceeded',

  // ===== Billing & Subscriptions =====
  PAYMENT_SUCCESS = 'payment.success',
  PAYMENT_FAILED = 'payment.failed',
  SUBSCRIPTION_CREATED = 'subscription.created',
  SUBSCRIPTION_EXPIRING = 'subscription.expiring',
  SUBSCRIPTION_EXPIRED = 'subscription.expired',
  SUBSCRIPTION_CANCELLED = 'subscription.cancelled',
  USAGE_LIMIT_WARNING = 'usage.limit_warning',
  USAGE_LIMIT_EXCEEDED = 'usage.limit_exceeded',

  // ===== System & Maintenance =====
  SYSTEM_MAINTENANCE_SCHEDULED = 'system.maintenance_scheduled',
  SYSTEM_MAINTENANCE_STARTING = 'system.maintenance_starting',
  SYSTEM_MAINTENANCE_COMPLETED = 'system.maintenance_completed',
  SYSTEM_DEGRADED = 'system.degraded',
  SYSTEM_RECOVERED = 'system.recovered',

  // ===== API & Integrations =====
  API_KEY_CREATED = 'api_key.created',
  API_KEY_EXPIRING = 'api_key.expiring',
  API_KEY_REVOKED = 'api_key.revoked',
  INTEGRATION_CONNECTED = 'integration.connected',
  INTEGRATION_FAILED = 'integration.failed',
  WEBHOOK_FAILED = 'webhook.failed',

  // ===== Domain Intelligence & Duplicate Detection =====
  DOMAIN_MATCH_DETECTED = 'domain_match.detected', // Existing business admin notified of domain match
  PARTNER_EXISTING_BUSINESS = 'partner.existing_business', // B2 admin notified of new partnership
  BUSINESS_OWNER_INVITED = 'business_owner.invited', // Business owner invited by platform admin

  // ===== Case Messaging =====
  LENDER_MESSAGE = 'case.lender_message', // Lender sent a message on a loan case

  // ===== Watchlist (Issue #1424) =====
  // A user pinned this entity to their Command Center watchlist; the
  // entity's status just changed and they want to know. v1: case/loan only.
  // TODO(#1424-followup): partner / consumer / application status changes.
  WATCHED_ENTITY_STATUS_CHANGED = 'watched_entity.status_changed',

  // ===== E-Signatures (Issue #1283) =====
  ESIGN_ENVELOPE_SENT = 'esign.envelope_sent',
  ESIGN_ENVELOPE_COMPLETED = 'esign.envelope_completed',
  ESIGN_ENVELOPE_VOIDED = 'esign.envelope_voided',
  ESIGN_ENVELOPE_EXPIRED = 'esign.envelope_expired',

  // ===== Custom Events =====
  CUSTOM = 'custom', // For business-defined events
}

/** Activity type used in emitCaseEvent data payload for lender messages */
export const ACTIVITY_TYPE_LENDER_MESSAGE = 'lender_message';

/**
 * Notification Channels
 * Where notifications can be delivered
 */
export enum NotificationChannel {
  EMAIL = 'email',
  SMS = 'sms',
  PUSH = 'push', // Firebase Cloud Messaging
  IN_APP = 'in_app', // In-app notifications
  WEBHOOK = 'webhook', // Outgoing webhooks
}

/**
 * Notification Status
 * Lifecycle states of a notification
 */
export enum NotificationStatus {
  PENDING = 'pending', // Created but not yet sent
  QUEUED = 'queued', // Queued for sending
  SENDING = 'sending', // Currently being sent
  SENT = 'sent', // Successfully sent
  DELIVERED = 'delivered', // Confirmed delivered (FCM only)
  READ = 'read', // User has read the notification
  FAILED = 'failed', // Failed to send
  EXPIRED = 'expired', // Expired before delivery
  CANCELLED = 'cancelled', // Manually cancelled
}

/**
 * Notification Severity
 * Priority/importance level of the notification
 */
export enum NotificationSeverity {
  INFO = 'info', // Informational
  WARNING = 'warning', // Warning/attention needed
  CRITICAL = 'critical', // Critical/urgent action required
  SUCCESS = 'success', // Success message
}

/**
 * Notification Type
 * Category of the notification
 */
export enum NotificationType {
  SYSTEM = 'system', // System-generated
  DOCUMENT = 'document', // Document-related
  AGENT = 'agent', // Agent-related
  BILLING = 'billing', // Billing/payment-related
  SECURITY = 'security', // Security-related
  USER = 'user', // User management
  INTEGRATION = 'integration', // Integration/API-related
  CUSTOM = 'custom', // Custom notifications
}

/**
 * Recipient Type
 * Who should receive the notification
 */
export enum RecipientType {
  BUSINESS_ADMINS = 'business_admins', // All admins in business
  ALL_USERS = 'all_users', // All users in business
  SPECIFIC_USERS = 'specific_users', // Specific user IDs
  USER_WITH_ROLE = 'user_with_role', // Users with specific role
  SUPER_ADMINS = 'super_admins', // Platform super admins
}

/**
 * Device Type
 * Type of device for FCM tokens
 */
export enum DeviceType {
  WEB = 'web',
  ANDROID = 'android',
  IOS = 'ios',
}

/**
 * Digest Frequency
 * How often to batch notifications in digest mode
 */
export enum DigestFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
}

// ============================================================================
// API NOTIFICATION CHANNEL (Uppercase values for API compatibility)
// ============================================================================

/**
 * Notification Channel values for API requests (uppercase)
 * The NotificationChannel enum uses lowercase values for internal use,
 * but gRPC APIs expect uppercase values. Use this enum for API schemas.
 */
export enum NotificationChannelAPI {
  EMAIL = 'EMAIL',
  SMS = 'SMS',
  PUSH = 'PUSH',
  IN_APP = 'IN_APP',
}
